import { getApiClient } from '@apps/web/lib/api_client';
import {
    type OutreachConflictError,
    parseCancelledShiftSelectionError,
    parseOutreachConflictError,
} from '@apps/web/lib/outreach_utils';
import {
    ApiError,
    type components,
    type Mode,
    type Outreach,
} from '@libraries/typescript/api_client';

export type QuickOutreachCreateBody =
    components['schemas']['QuickCalloutCreate'];

export type QuickOutreachType = Extract<
    QuickOutreachCreateBody['outreach_type'],
    'callout' | 'recurring_shift'
>;

export type QuickOutreachCreationFailure =
    | {
          readonly kind: 'cancelled_shift';
          readonly message: string;
          readonly shiftIds: ReadonlyArray<string>;
      }
    | {
          readonly kind: 'outreach_conflict';
          readonly conflict: OutreachConflictError;
      }
    | { readonly kind: 'request'; readonly message: string };

const MISSING_TRANSFER_NUMBER_MESSAGE =
    'Please add a transfer option in Settings > Organization before starting outreach calls!';

type QuickOutreachVoiceTemplate = {
    readonly steps: ReadonlyArray<{ readonly step_type: string }>;
};

/**
 * Quick callouts use the saved template when one exists and otherwise use the
 * built-in template, which includes a voice step.
 */
export function quickOutreachWillHaveVoiceStep(
    selectedTemplate: QuickOutreachVoiceTemplate | undefined,
): boolean {
    return selectedTemplate
        ? selectedTemplate.steps.some((step) => step.step_type === 'voice')
        : true;
}

export function getQuickOutreachTransferError(
    selectedTemplate: QuickOutreachVoiceTemplate | undefined,
    defaultTransferNumber: string | null | undefined,
): string | null {
    return quickOutreachWillHaveVoiceStep(selectedTemplate) &&
        !defaultTransferNumber
        ? MISSING_TRANSFER_NUMBER_MESSAGE
        : null;
}

export async function createQuickOutreach({
    organizationId,
    body,
    reason,
    mode,
}: {
    readonly organizationId: string;
    readonly body: QuickOutreachCreateBody;
    readonly reason: string;
    readonly mode?: Mode;
}): Promise<Outreach> {
    return getApiClient().post({
        url: '/organizations/{organization_id}/outreaches/quick-callout',
        pathParams: { organization_id: organizationId },
        body,
        reason,
        ...(mode ? { mode } : {}),
    });
}

export function parseQuickOutreachCreationFailure(
    error: unknown,
    fallbackMessage: string,
): QuickOutreachCreationFailure {
    const cancelledSelection = parseCancelledShiftSelectionError(error);
    if (cancelledSelection) {
        return {
            kind: 'cancelled_shift',
            message: cancelledSelection.message,
            shiftIds: cancelledSelection.shiftIds,
        };
    }

    const conflict = parseOutreachConflictError(error);
    if (conflict) {
        return { kind: 'outreach_conflict', conflict };
    }

    const detail =
        error instanceof ApiError
            ? (error.body as { readonly detail?: unknown })?.detail
            : null;
    return {
        kind: 'request',
        message: typeof detail === 'string' ? detail : fallbackMessage,
    };
}
